import { Drawer, Skeleton, Text, UnstyledButton } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { VideoItemsResponse } from '@nelnela/contracts';
import { api } from '../../lib/api';
import { glyphFor } from '../../lib/marks';
import { SCOPED_RUN_MAX } from '../../lib/review-run';

/**
 * The scene / episode vocabulary preview (M6a step 8) — what is in this stretch of the episode,
 * before you watch it.
 *
 * Two rules shape the whole sheet.
 *
 * **Opening it emits nothing.** A list of words is not reading in context: the learner is
 * scanning a menu, not meeting a word in a sentence, and an `exposure` written here would tell
 * the memory model they had met every word in the scene when they had done no such thing. Only a
 * TAP does anything, and what it does is open the gloss — which the gloss route logs server-side
 * as a `lookup`, exactly as it does from the transcript.
 *
 * **It is a reason to watch, not homework.** Due-first ordering, one line per word, and a single
 * "Review these" for the ones that are due. There is no progress, no count of what you have not
 * learned, and nothing to finish.
 */

export interface VocabSheetProps {
  opened: boolean;
  onClose: () => void;
  videoId: string;
  /** the span, or undefined for the whole episode (Watch's ⋯ "Words in this episode") */
  startMs?: number;
  endMs?: number;
  /** what the sheet is looking at, for its own title */
  label: string;
  /** tap a row → the item's card in the gloss drawer */
  onOpenItem: (itemId: string) => void;
}

export function VocabSheet({
  opened,
  onClose,
  videoId,
  startMs,
  endMs,
  label,
  onOpenItem,
}: VocabSheetProps) {
  // Keyed on the span so re-opening the sheet on a different scene refetches rather than showing
  // the last one's words. `enabled` keeps a closed sheet from fetching at all — the scenes bar
  // renders one of these per scene affordance, and a preview nobody opened is a query nobody
  // asked for.
  const q = useQuery({
    queryKey: ['video-items', videoId, startMs ?? null, endMs ?? null],
    queryFn: () =>
      api.videoItems(videoId, {
        ...(startMs !== undefined ? { startMs } : {}),
        ...(endMs !== undefined ? { endMs } : {}),
      }),
    enabled: opened,
  });

  const data = q.data;
  const dueIds = (data?.items ?? []).filter((i) => i.due).map((i) => i.itemId);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="72%"
      withCloseButton={false}
      title={undefined}
      styles={{
        content: {
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          borderTop: '1px solid var(--nn-hairline)',
          background: 'var(--nn-panel)',
        },
        body: { padding: 0 },
      }}
      transitionProps={{ duration: 160 }}
    >
      <div
        aria-hidden
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: 'var(--nn-hairline)',
          margin: '8px auto 4px',
        }}
      />
      <div style={{ padding: '4px 16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 14, color: 'var(--nn-text-strong)' }}>{label}</span>
          <span style={{ flex: 1 }} />
          {data && <Counts counts={data.counts} />}
        </div>

        {q.isPending ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 14 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={16} radius="sm" />
            ))}
          </div>
        ) : q.error || !data ? (
          <Text fz={12} c="var(--nn-muted)" mt={14}>
            Couldn’t load the words for this scene.
          </Text>
        ) : data.items.length === 0 ? (
          <Text fz={12} c="var(--nn-muted)" mt={14}>
            Nothing tagged here yet — the resolver gets to it after the transcript is cleaned up.
          </Text>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: 10 }}>
              {data.items.map((it) => (
                <UnstyledButton
                  key={it.itemId}
                  onClick={() => onOpenItem(it.itemId)}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 9,
                    padding: '7px 0',
                    borderBottom: '1px solid var(--nn-hairline)',
                    textAlign: 'left',
                  }}
                >
                  {/* The same ○ ◐ ● the reading surface draws, so the two never disagree. */}
                  <span
                    aria-hidden
                    style={{ fontSize: 11, color: 'var(--nn-faint)', width: 12, flexShrink: 0 }}
                  >
                    {glyphFor(it.memory)}
                  </span>
                  <span
                    lang="ka"
                    style={{
                      fontFamily: "'Noto Serif Georgian', serif",
                      fontSize: 14.5,
                      color: 'var(--nn-text-strong)',
                      flexShrink: 0,
                    }}
                  >
                    {it.display}
                  </span>
                  {it.gloss && (
                    <span
                      style={{
                        fontSize: 11.5,
                        color: 'var(--nn-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.gloss}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  {it.occurrences > 1 && (
                    <span style={{ fontSize: 10.5, color: 'var(--nn-faint)', flexShrink: 0 }}>
                      ×{it.occurrences}
                    </span>
                  )}
                </UnstyledButton>
              ))}
            </div>

            {dueIds.length > 0 && (
              <Link
                to={reviewHref(videoId, startMs, endMs)}
                onClick={onClose}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginTop: 14,
                  padding: '8px 16px',
                  borderRadius: 8,
                  background: 'var(--nn-accent)',
                  color: 'var(--nn-on-accent)',
                  fontSize: 12.5,
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Review these ({Math.min(SCOPED_RUN_MAX, dueIds.length)})
              </Link>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

/**
 * The span, not the item ids, goes in the link.
 *
 * A URL carrying twelve uuids is both enormous and stale the moment the fold runs — a word that
 * fell due while the sheet was open would be missing from it. The span is small, it is the thing
 * the learner actually chose, and the server re-reads what is due inside it at the moment the run
 * is built.
 */
/*
 * The count is capped at what the run will actually be. `scopedItemIds` asks for `min(8, due)`,
 * so an episode with fourteen due words offered "Review these (14)" and delivered eight — the
 * header's "1 of 8" was the only hint, and it never said the other six were left behind.
 */
export function reviewHref(videoId: string, startMs?: number, endMs?: number): string {
  const p = new URLSearchParams({ video: videoId });
  if (startMs !== undefined) p.set('startMs', String(Math.round(startMs)));
  if (endMs !== undefined) p.set('endMs', String(Math.round(endMs)));
  return `/review?${p.toString()}`;
}

/** "6 due · 18 words" — the only numbers on the sheet, and neither is a score. */
function Counts({ counts }: { counts: VideoItemsResponse['counts'] }) {
  return (
    <span style={{ fontSize: 10.5, color: 'var(--nn-faint)' }}>
      {counts.due > 0 ? `${counts.due} due · ` : ''}
      {counts.tagged} {counts.tagged === 1 ? 'word' : 'words'}
    </span>
  );
}
